import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { FileText, Send, CheckCircle, XCircle, Clock, Eye, Download, Wallet, AlertCircle, User, Calendar, ClockIcon, Briefcase, PartyPopper, Palmtree, FileQuestion, BanknoteIcon, AlertTriangle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';

const HRRequests = () => {
  const [activeTab, setActiveTab] = useState('available');
  const [availableForms, setAvailableForms] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [sentDocuments, setSentDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showFillFormModal, setShowFillFormModal] = useState(false);
  const [showRequestDetailModal, setShowRequestDetailModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({});
  const [formNotes, setFormNotes] = useState('');
  
  // Employee info for avans validation
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [loadingEmployeeInfo, setLoadingEmployeeInfo] = useState(false);
  // İzin bakiyesi & hesaplama
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [leaveCalc, setLeaveCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  
  // Document response modal states
  const [showDocResponseModal, setShowDocResponseModal] = useState(false);
  const [selectedDocRequest, setSelectedDocRequest] = useState(null);
  const [responseFiles, setResponseFiles] = useState([]);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseLoading, setResponseLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // İzin bakiyesini getir (yıllık izin seçilince)
  const fetchLeaveBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await fetch(`${API_URL}/api/employee/me/leave-balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setLeaveBalance(await res.json());
    } catch (e) {
      console.error('İzin bakiyesi alınamadı:', e);
    }
  }, [API_URL]);

  // Seçilen tarih aralığı için kurallara göre gün hesabı
  const calculateLeave = useCallback(async (start, end) => {
    if (!start || !end) { setLeaveCalc(null); return; }
    setCalcLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await fetch(`${API_URL}/api/employee/me/leave-calculation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: start, end_date: end }),
      });
      if (res.ok) setLeaveCalc(await res.json());
      else setLeaveCalc(null);
    } catch (e) {
      console.error('İzin hesaplanamadı:', e);
      setLeaveCalc(null);
    } finally {
      setCalcLoading(false);
    }
  }, [API_URL]);

  // Yıllık izin seçilince bakiye çek; tarih değişince hesapla
  useEffect(() => {
    if (selectedForm?.id === 'leave' && formData.izinTuru === 'yillik') {
      fetchLeaveBalance();
      calculateLeave(formData.izinBaslangic, formData.izinBitis);
    } else {
      setLeaveCalc(null);
    }
  }, [selectedForm, formData.izinTuru, formData.izinBaslangic, formData.izinBitis, fetchLeaveBalance, calculateLeave]);

  // Okundu olarak işaretle
  const markAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      await fetch(`${API_URL}/api/notifications/mark-read?item_type=hr_request&mark_all=true&category=my_hr_requests`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Okundu işaretleme hatası:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchAvailableForms();
    fetchMyRequests();
    fetchDocumentRequests();
    fetchSentDocuments();
    // Sayfa açıldığında okundu olarak işaretle
    markAsRead();
  }, [markAsRead]);
  
  const fetchDocumentRequests = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/employee/document-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentRequests(data);
      }
    } catch (error) {
      console.error('İK belge talepleri yüklenemedi:', error);
    }
  };

  const fetchSentDocuments = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/employee/sent-documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSentDocuments(data);
      }
    } catch (error) {
      console.error('Gönderilen belgeler yüklenemedi:', error);
    }
  };

  // Handle opening document response modal
  const handleOpenDocResponse = (docRequest) => {
    setSelectedDocRequest(docRequest);
    setResponseFiles([]);
    setResponseMessage('');
    setShowDocResponseModal(true);
  };

  // Handle file selection for document response
  const handleResponseFileChange = (e) => {
    const files = Array.from(e.target.files);
    const readFiles = [];
    let processed = 0;
    
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        readFiles.push({
          name: file.name,
          type: file.type,
          data: reader.result
        });
        processed++;
        if (processed === files.length) {
          setResponseFiles(prev => [...prev, ...readFiles]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle removing a file from response
  const handleRemoveResponseFile = (index) => {
    setResponseFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Submit document response to HR
  const handleSubmitDocResponse = async () => {
    if (!selectedDocRequest) return;
    
    if (responseFiles.length === 0) {
      toast.error('Lütfen en az bir belge yükleyin');
      return;
    }

    setResponseLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/employee/document-requests/${selectedDocRequest.id}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documents: responseFiles,
          message: responseMessage
        })
      });

      if (response.ok) {
        toast.success('Belgeleriniz başarıyla gönderildi');
        setShowDocResponseModal(false);
        setSelectedDocRequest(null);
        setResponseFiles([]);
        setResponseMessage('');
        fetchDocumentRequests(); // Refresh list
        fetchSentDocuments(); // Refresh sent documents
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Belgeler gönderilemedi');
      }
    } catch (error) {
      console.error('Belge gönderme hatası:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setResponseLoading(false);
    }
  };

  const fetchAvailableForms = async () => {
    try {
      const token = localStorage.getItem('fortis_token'); // FIXED: Use correct key
      const response = await fetch(`${API_URL}/api/form-templates/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableForms(data);
      }
    } catch (error) {
      console.error('Formlar yüklenemedi:', error);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const token = localStorage.getItem('fortis_token'); // FIXED: Use correct key
      const response = await fetch(`${API_URL}/api/hr-requests/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
      }
    } catch (error) {
      console.error('Taleplerim yüklenemedi:', error);
    }
  };

  // Fetch employee info for avans validation
  const fetchEmployeeInfo = async () => {
    setLoadingEmployeeInfo(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/employee/me/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeInfo(data);
        return data;
      }
    } catch (error) {
      console.error('Çalışan bilgisi alınamadı:', error);
    } finally {
      setLoadingEmployeeInfo(false);
    }
    return null;
  };

  // En yakın Çarşamba gününü hesapla (bugün Çarşamba ise haftaya Çarşamba)
  const getNextWednesday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Pazar, 1=Pazartesi, ..., 3=Çarşamba, ...
    let daysUntilWednesday;
    
    if (dayOfWeek === 3) {
      // Bugün Çarşamba ise haftaya Çarşamba (7 gün sonra)
      daysUntilWednesday = 7;
    } else if (dayOfWeek < 3) {
      // Pazar, Pazartesi, Salı ise bu hafta Çarşamba
      daysUntilWednesday = 3 - dayOfWeek;
    } else {
      // Perşembe, Cuma, Cumartesi ise gelecek hafta Çarşamba
      daysUntilWednesday = 10 - dayOfWeek;
    }
    
    const nextWednesday = new Date(today);
    nextWednesday.setDate(today.getDate() + daysUntilWednesday);
    return nextWednesday.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleOpenFillForm = async (form) => {
    setSelectedForm(form);
    
    // Fetch employee info for all forms
    const info = await fetchEmployeeInfo();
    
    // Set form data with current date
    const now = new Date();
    const formattedDate = now.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Set default values based on form type
    if (form.id === 'avans') {
      setFormData({ 
        avansMiktari: '', 
        taksitSayisi: '', 
        avansSebebi: '',
        talepEdenAdi: info?.full_name || '',
        talepTarihi: formattedDate,
        odemeTarihi: getNextWednesday(),
        islakImzaBelgesi: null
      });
    } else if (form.id === 'overtime') {
      setFormData({
        mesaiYapanAdi: info?.full_name || '',
        formOlusturmaTarihi: formattedDate,
        mesaiTarihi: '',
        mesaiSaati: '',
        mesaiGunu: '', // 'tatil' or 'is_gunu'
        mesaiNedeni: ''
      });
    } else if (form.id === 'leave') {
      setFormData({
        talepEdenAdi: info?.full_name || '',
        talepTarihi: formattedDate,
        izinTuru: '', // 'yillik', 'mazeret', 'ucretsiz'
        izinBaslangic: '',
        izinBitis: ''
      });
    } else {
      setFormData({
        talepEdenAdi: info?.full_name || '',
        talepTarihi: formattedDate
      });
    }
    setFormNotes('');
    setShowFillFormModal(true);
  };

  const handleSubmitRequest = async () => {
    if (!selectedForm) return;
    
    // Form-specific validation
    if (selectedForm.id === 'avans') {
      // Check if employee can request advance
      if (employeeInfo && !employeeInfo.can_request_advance) {
        toast.error(employeeInfo.advance_validation_error || 'Avans talebinde bulunamazsınız');
        return;
      }
      
      if (!formData.avansMiktari || formData.avansMiktari <= 0) {
        toast.error('Lütfen geçerli bir avans miktarı girin');
        return;
      }
      
      // Check max advance amount (30% of salary)
      if (employeeInfo?.max_advance_amount && parseInt(formData.avansMiktari) > employeeInfo.max_advance_amount) {
        toast.error(`Bir defa çekilebilecek en yüksek miktardan fazla talep ettiniz. (Max: ${employeeInfo.max_advance_amount.toLocaleString('tr-TR')} TL)`);
        return;
      }
      
      if (!formData.taksitSayisi) {
        toast.error('Lütfen taksit sayısını seçin');
        return;
      }
      
      // Check max installment (kurallardan)
      const maxTaksit = employeeInfo?.max_taksit_sayisi || 0;
      if (maxTaksit > 0 && parseInt(formData.taksitSayisi) > maxTaksit) {
        toast.error(`Taksit sayısı en fazla ${maxTaksit} olabilir`);
        return;
      }
      
      if (!formData.avansSebebi || formData.avansSebebi.trim() === '') {
        toast.error('Lütfen avans sebebini belirtin');
        return;
      }
      
      // Islak imzalı belge zorunlu
      if (!formData.islakImzaBelgesi) {
        toast.error('Lütfen ıslak imzalı formu yükleyin');
        return;
      }
    } else if (selectedForm.id === 'overtime') {
      // Fazla Mesai Formu validation
      if (!formData.mesaiTarihi) {
        toast.error('Lütfen mesai yapılan tarihi girin');
        return;
      }
      
      if (!formData.mesaiSaati || formData.mesaiSaati <= 0) {
        toast.error('Lütfen mesai saatini girin');
        return;
      }
      
      if (!formData.mesaiGunu) {
        toast.error('Lütfen mesai gününü seçin (Tatil veya İş Günü)');
        return;
      }
      
      if (!formData.mesaiNedeni || formData.mesaiNedeni.trim() === '') {
        toast.error('Lütfen mesai nedenini belirtin');
        return;
      }
      
      if (!formData.mesaiFormDosya) {
        toast.error('Lütfen ıslak imzalı formu yükleyin');
        return;
      }
    } else if (selectedForm.id === 'leave') {
      // İzin Talep Formu validation
      if (!formData.izinTuru) {
        toast.error('Lütfen izin türünü seçin');
        return;
      }
      
      if (!formData.izinBaslangic) {
        toast.error('Lütfen izin başlangıç tarihini girin');
        return;
      }
      
      if (!formData.izinBitis) {
        toast.error('Lütfen izin bitiş tarihini girin');
        return;
      }
      
      // Check if end date is after start date
      if (formData.izinBaslangic && formData.izinBitis && formData.izinBitis < formData.izinBaslangic) {
        toast.error('Bitiş tarihi başlangıç tarihinden önce olamaz');
        return;
      }
      
      // Islak imzalı belge zorunlu
      if (!formData.izinBelgeleri || formData.izinBelgeleri.length === 0) {
        toast.error('Lütfen ıslak imzalı formu yükleyin');
        return;
      }

      // Yıllık izin aşımı varsa ücretli/ücretsiz seçimi zorunlu
      if (formData.izinTuru === 'yillik' && leaveCalc && leaveCalc.exceeds && !formData.asanTuru) {
        toast.error('Yıllık izninizi aşan günler için Ücretli veya Ücretsiz İzin seçin');
        return;
      }

      // Hesaplama verilerini forma ekle (onay ekranlarında görünür)
      if (formData.izinTuru === 'yillik' && leaveCalc) {
        formData.hesaplananGun = leaveCalc.counted_days;
        formData.toplamTakvimGun = leaveCalc.total_calendar_days;
        formData.yillikDusulenGun = leaveCalc.annual_portion;
        formData.asanGun = leaveCalc.excess_days;
        formData.kalanYillikIzin = leaveCalc.remaining_annual_leave;
        if (!leaveCalc.exceeds) {
          formData.asanTuru = '';
          formData.asanTuruLabel = '';
        } else {
          const labelMap = { avans: 'Avans Yıllık İzin', ucretli: 'Ücretli İzin', ucretsiz: 'Ücretsiz İzin' };
          formData.asanTuruLabel = labelMap[formData.asanTuru] || '';
        }
      }
    } else {
      // Generic validation for other forms
      if (Object.keys(formData).length === 0) {
        toast.error('Lütfen en az bir form alanını doldurun');
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token'); // FIXED: Use correct key
      const response = await fetch(
        `${API_URL}/api/hr-requests?form_template_id=${selectedForm.id}&notes=${encodeURIComponent(formNotes)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ form_data: formData })
        }
      );
      
      if (response.ok) {
        toast.success('Talebiniz gönderildi');
        setShowFillFormModal(false);
        setSelectedForm(null);
        setFormData({});
        setFormNotes('');
        fetchMyRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Talep gönderilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowRequestDetailModal(true);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending_branch': { label: 'Şube Onayı Bekliyor', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      'pending_hr': { label: 'İK Onayı Bekliyor', className: 'bg-blue-100 text-blue-800 border-blue-200' },
      'pending_management': { label: 'Yönetim Onayı Bekliyor', className: 'bg-purple-100 text-purple-800 border-purple-200' },
      'pending_accounting': { label: 'Kabul Edildi · Ödeme Sürecinde', className: 'bg-green-100 text-green-800 border-green-200' },
      'avans_given': { label: 'Avans Verildi', className: 'bg-green-100 text-green-800 border-green-200' },
      'completed': { label: 'Tamamlandı', className: 'bg-green-100 text-green-800 border-green-200' },
      'approved': { label: 'Onaylandı', className: 'bg-green-100 text-green-800 border-green-200' },
      'rejected_by_branch': { label: 'Şube Reddetti', className: 'bg-red-100 text-red-800 border-red-200' },
      'rejected_by_hr': { label: 'İK Reddetti', className: 'bg-red-100 text-red-800 border-red-200' },
      'rejected_by_management': { label: 'Yönetim Reddetti', className: 'bg-red-100 text-red-800 border-red-200' }
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800 border-gray-200' };
    return <Badge variant="outline" className={statusInfo.className}>{statusInfo.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const approvedRequests = myRequests.filter(r => 
    r.status === 'approved' || 
    r.status === 'pending_accounting' || 
    r.status === 'avans_given' || 
    r.status === 'completed'
  );
  const rejectedRequests = myRequests.filter(r => 
    r.status === 'rejected_by_branch' || 
    r.status === 'rejected_by_hr' || 
    r.status === 'rejected_by_management'
  );
  const pendingRequests = myRequests.filter(r => 
    r.status === 'pending_branch' || 
    r.status === 'pending_hr' || 
    r.status === 'pending_management'
  );

  // Gönderilen belgeleri ayır: reddedilenleri "Red Edilen" sekmesine taşı
  const activeSentDocuments = sentDocuments.filter(d => d.status !== 'rejected');
  const rejectedSentDocuments = sentDocuments.filter(d => d.status === 'rejected');

  return (
    <div className="space-y-6" data-testid="hr-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>İnsan Kaynaklarından Talepler</h1>
          <p className="text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'Karla, sans-serif' }}>Form talepleri oluşturun ve takip edin</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="available" data-testid="tab-available" className="data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Mevcut Formlar</span>
            <span className="sm:hidden">Formlar</span>
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending" className="data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white">
            <Clock className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Bekleyen</span>
            {pendingRequests.length > 0 && (
              <Badge className="ml-1 bg-yellow-500 text-white border-0">{pendingRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved" className="data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white">
            <CheckCircle className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Kabul Edilen</span>
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected" className="data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white">
            <XCircle className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Red Edilen</span>
            {(rejectedRequests.length + rejectedSentDocuments.length) > 0 && (
              <Badge className="ml-1 bg-red-500 text-white border-0">{rejectedRequests.length + rejectedSentDocuments.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="hr-documents" data-testid="tab-hr-documents" className="data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white">
            <FileQuestion className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">İK Talepleri</span>
            {documentRequests.filter(d => !d.has_responded).length > 0 && (
              <Badge className="ml-1 bg-orange-500 text-white border-0 animate-pulse">
                {documentRequests.filter(d => !d.has_responded).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent-documents" data-testid="tab-sent-documents" className="data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white">
            <Send className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Gönderilenler</span>
            {activeSentDocuments.length > 0 && (
              <Badge className="ml-1 bg-green-500 text-white border-0">{activeSentDocuments.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Available Forms Tab */}
        <TabsContent value="available">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Doldurulabilir Formlar</CardTitle>
              <CardDescription style={{ fontFamily: 'Karla, sans-serif' }}>Aşağıdaki formları doldurarak İK'ya talep gönderebilirsiniz</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {/* Avans Talep Formu */}
                <Button
                  variant="outline"
                  className="h-auto p-6 flex flex-col items-center justify-center gap-3 border-2 border-[hsl(var(--brand-300))] hover:bg-[hsl(var(--brand-50))] hover:border-[hsl(var(--brand-500))] transition-all"
                  onClick={() => handleOpenFillForm({ id: 'avans', name: 'Avans Talep Formu' })}
                  data-testid="form-avans"
                >
                  <Wallet className="h-8 w-8 text-[hsl(var(--brand-700))]" />
                  <span className="font-semibold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Avans Talep Formu
                  </span>
                </Button>

                {/* Fazla Mesai Formu */}
                <Button
                  variant="outline"
                  className="h-auto p-6 flex flex-col items-center justify-center gap-3 border-2 border-[hsl(var(--brand-300))] hover:bg-[hsl(var(--brand-50))] hover:border-[hsl(var(--brand-500))] transition-all"
                  onClick={() => handleOpenFillForm({ id: 'overtime', name: 'Fazla Mesai Formu' })}
                  data-testid="form-overtime"
                >
                  <Clock className="h-8 w-8 text-[hsl(var(--brand-700))]" />
                  <span className="font-semibold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Fazla Mesai Formu
                  </span>
                </Button>

                {/* İzin Talep Formu */}
                <Button
                  variant="outline"
                  className="h-auto p-6 flex flex-col items-center justify-center gap-3 border-2 border-[hsl(var(--brand-300))] hover:bg-[hsl(var(--brand-50))] hover:border-[hsl(var(--brand-500))] transition-all"
                  onClick={() => handleOpenFillForm({ id: 'leave', name: 'İzin Talep Formu' })}
                  data-testid="form-leave"
                >
                  <CheckCircle className="h-8 w-8 text-[hsl(var(--brand-700))]" />
                  <span className="font-semibold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    İzin Talep Formu
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Requests Tab */}
        <TabsContent value="pending">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Bekleyen Taleplerim</CardTitle>
              <CardDescription style={{ fontFamily: 'Karla, sans-serif' }}>Onay bekleyen talepleriniz</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingRequests.length === 0 ? (
                <div className="text-center py-16 text-[hsl(var(--muted-foreground))]" data-testid="empty-pending-state">
                  <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="font-medium" style={{ fontFamily: 'Karla, sans-serif' }}>Bekleyen talebiniz bulunmuyor</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div 
                      key={request.id}
                      className="p-4 border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                      onClick={() => handleViewRequest(request)}
                      data-testid={`pending-request-${request.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {request.form_number && (
                              <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-0.5 rounded">
                                #{request.form_number}
                              </span>
                            )}
                            <h3 className="font-semibold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>{request.form_template_name}</h3>
                            {getStatusBadge(request.status)}
                          </div>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            Gönderilme: {formatDate(request.created_at)}
                          </p>
                        </div>
                        <Eye className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved Requests Tab */}
        <TabsContent value="approved">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Kabul Edilen Taleplerim</CardTitle>
              <CardDescription style={{ fontFamily: 'Karla, sans-serif' }}>Onaylanmış talepleriniz</CardDescription>
            </CardHeader>
            <CardContent>
              {approvedRequests.length === 0 ? (
                <div className="text-center py-16 text-[hsl(var(--muted-foreground))]" data-testid="empty-approved-state">
                  <CheckCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="font-medium" style={{ fontFamily: 'Karla, sans-serif' }}>Onaylanmış talebiniz bulunmuyor</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {approvedRequests.map((request) => (
                    <div 
                      key={request.id}
                      className="p-4 border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                      onClick={() => handleViewRequest(request)}
                      data-testid={`approved-request-${request.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {request.form_number && (
                              <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-0.5 rounded">
                                #{request.form_number}
                              </span>
                            )}
                            <h3 className="font-semibold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>{request.form_template_name}</h3>
                            {request.avans_recorded_to_employee
                              ? getStatusBadge('avans_given')
                              : getStatusBadge(request.status)}
                          </div>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            Gönderilme: {formatDate(request.created_at)}
                          </p>
                        </div>
                        <Eye className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rejected Requests Tab */}
        <TabsContent value="rejected">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Red Edilen Taleplerim</CardTitle>
              <CardDescription style={{ fontFamily: 'Karla, sans-serif' }}>Reddedilmiş talepleriniz ve belgeleriniz</CardDescription>
            </CardHeader>
            <CardContent>
              {rejectedRequests.length === 0 && rejectedSentDocuments.length === 0 ? (
                <div className="text-center py-16 text-[hsl(var(--muted-foreground))]" data-testid="empty-rejected-state">
                  <XCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="font-medium" style={{ fontFamily: 'Karla, sans-serif' }}>Reddedilmiş talebiniz bulunmuyor</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Reddedilen form talepleri */}
                  {rejectedRequests.map((request) => (
                    <div 
                      key={request.id}
                      className="p-4 border border-[hsl(var(--border))] rounded-lg hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                      onClick={() => handleViewRequest(request)}
                      data-testid={`rejected-request-${request.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {request.form_number && (
                              <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))] px-2 py-0.5 rounded">
                                #{request.form_number}
                              </span>
                            )}
                            <h3 className="font-semibold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Chivo, sans-serif' }}>{request.form_template_name}</h3>
                            {getStatusBadge(request.status)}
                          </div>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            Gönderilme: {formatDate(request.created_at)}
                          </p>
                          {request.branch_rejection?.reason && (
                            <p className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                              <strong>Şube Red Nedeni:</strong> {request.branch_rejection.reason}
                            </p>
                          )}
                          {request.hr_rejection?.reason && (
                            <p className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                              <strong>İK Red Nedeni:</strong> {request.hr_rejection.reason}
                            </p>
                          )}
                          {request.management_rejection?.reason && (
                            <p className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                              <strong>Yönetim Red Nedeni:</strong> {request.management_rejection.reason}
                            </p>
                          )}
                        </div>
                        <Eye className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                      </div>
                    </div>
                  ))}

                  {/* Reddedilen gönderilen belgeler */}
                  {rejectedSentDocuments.map((doc) => (
                    <Card key={doc.id} className="border-l-4 border-l-red-500" data-testid={`rejected-sent-doc-${doc.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="h-4 w-4 text-red-600" />
                              <h3 className="font-semibold text-gray-800">{doc.request_title}</h3>
                              <Badge className="bg-red-100 text-red-700">Reddedildi</Badge>
                            </div>
                            {doc.rejection_reason && (
                              <div className="bg-red-50 border border-red-200 rounded p-2 mb-2 text-sm text-red-700">
                                <span className="font-medium">Red Sebebi:</span> {doc.rejection_reason}
                                {doc.rejected_by && <span className="text-xs ml-2">- {doc.rejected_by}</span>}
                              </div>
                            )}
                            {doc.message && (
                              <p className="text-sm text-gray-600 mb-2">
                                <span className="font-medium">Mesaj:</span> {doc.message}
                              </p>
                            )}
                            <p className="text-xs text-gray-500">
                              Gönderim Tarihi: {new Date(doc.sent_at).toLocaleString('tr-TR')}
                            </p>
                            {doc.documents && doc.documents.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <p className="text-sm font-medium text-gray-700">Gönderilen Dosyalar:</p>
                                <div className="flex flex-wrap gap-2">
                                  {doc.documents.map((file, idx) => (
                                    <Button
                                      key={idx}
                                      variant="outline"
                                      size="sm"
                                      className="gap-2"
                                      onClick={() => {
                                        if (file.data) {
                                          const link = document.createElement('a');
                                          link.href = file.data;
                                          link.download = file.name || `belge-${idx + 1}`;
                                          link.click();
                                        }
                                      }}
                                    >
                                      <Download className="h-3 w-3" />
                                      {file.name || `Belge ${idx + 1}`}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* İK'dan Gelen Talepler Tab */}
        <TabsContent value="hr-documents">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>İK'dan Gelen Belgeler</CardTitle>
              <CardDescription style={{ fontFamily: 'Karla, sans-serif' }}>
                İK birimi tarafından sizden istenen belgeler
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documentRequests.length === 0 ? (
                <div className="text-center py-12" data-testid="hr-documents-empty">
                  <FileQuestion className="h-12 w-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-30" />
                  <p className="text-[hsl(var(--muted-foreground))]">İK'dan gelen talep bulunmuyor</p>
                </div>
              ) : documentRequests.filter(d => !d.has_responded).length === 0 ? (
                <div className="text-center py-12" data-testid="hr-documents-all-responded">
                  <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-300" />
                  <h3 className="text-xl font-bold mb-2 text-gray-600">Tüm Belgeler Gönderildi</h3>
                  <p className="text-gray-500">Bekleyen belge talebiniz bulunmuyor. Gönderilen belgelerinizi "Gönderilenler" sekmesinde görebilirsiniz.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documentRequests.filter(d => !d.has_responded).map((docRequest) => (
                    <div
                      key={docRequest.id}
                      className="p-4 rounded-lg border transition-all hover:shadow-md bg-orange-50 border-orange-200"
                      data-testid={`hr-doc-request-${docRequest.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-5 w-5 text-[hsl(var(--brand-600))]" />
                            <h3 className="font-semibold">{docRequest.title}</h3>
                            <Badge className="bg-orange-500 animate-pulse">
                              {docRequest.pending_count > 1 ? `${docRequest.pending_count} Bekliyor` : 'Bekliyor'}
                            </Badge>
                          </div>
                          {docRequest.description && (
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">
                              {docRequest.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-[hsl(var(--muted-foreground))]">
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {docRequest.created_by_name || 'İK'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {new Date(docRequest.created_at).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                          {docRequest.documents && docRequest.documents.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {docRequest.documents.map((doc, idx) => (
                                <Button
                                  key={idx}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = doc.data;
                                    link.download = doc.name;
                                    link.click();
                                  }}
                                  data-testid={`download-doc-${idx}`}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  {doc.name}
                                </Button>
                              ))}
                            </div>
                          )}
                          
                          {/* Response Button */}
                          <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
                            {docRequest.has_responded ? (
                              <p className="text-sm text-green-700 flex items-center gap-1">
                                <CheckCircle className="h-4 w-4" />
                                Belgeniz gönderildi
                              </p>
                            ) : (
                              <Button
                                onClick={() => handleOpenDocResponse(docRequest)}
                                className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white"
                                data-testid={`respond-doc-${docRequest.id}`}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Belge Gönder
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gönderilenler Tab */}
        <TabsContent value="sent-documents">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Gönderilen Belgeler</CardTitle>
              <CardDescription>
                İK birimine gönderdiğiniz belgeler
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeSentDocuments.length === 0 ? (
                <div className="text-center py-12" data-testid="sent-documents-empty">
                  <Send className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-bold mb-2 text-gray-600">Henüz Belge Göndermediniz</h3>
                  <p className="text-gray-500">İK taleplerini yanıtladığınızda gönderilen belgeler burada görünecek</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeSentDocuments.map((doc) => (
                    <Card key={doc.id} className="border-l-4 border-l-green-500" data-testid={`sent-doc-${doc.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Send className="h-4 w-4 text-green-600" />
                              <h3 className="font-semibold text-gray-800">{doc.request_title}</h3>
                              <Badge className="bg-green-100 text-green-700">Gönderildi</Badge>
                            </div>
                            {doc.message && (
                              <p className="text-sm text-gray-600 mb-2">
                                <span className="font-medium">Mesaj:</span> {doc.message}
                              </p>
                            )}
                            <p className="text-xs text-gray-500">
                              Gönderim Tarihi: {new Date(doc.sent_at).toLocaleString('tr-TR')}
                            </p>
                            
                            {/* Gönderilen Dosyalar */}
                            {doc.documents && doc.documents.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <p className="text-sm font-medium text-gray-700">Gönderilen Dosyalar:</p>
                                <div className="flex flex-wrap gap-2">
                                  {doc.documents.map((file, idx) => (
                                    <Button
                                      key={idx}
                                      variant="outline"
                                      size="sm"
                                      className="gap-2"
                                      onClick={() => {
                                        if (file.data) {
                                          const link = document.createElement('a');
                                          link.href = file.data;
                                          link.download = file.name || `belge-${idx + 1}`;
                                          link.click();
                                        }
                                      }}
                                    >
                                      <Download className="h-3 w-3" />
                                      {file.name || `Belge ${idx + 1}`}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Fill Form Modal */}
      <Dialog open={showFillFormModal} onOpenChange={setShowFillFormModal}>
        <DialogContent className="max-w-2xl" data-testid="fill-form-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {selectedForm?.name} - Talep Oluştur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4" style={{ fontFamily: 'Karla, sans-serif' }}>
            {/* Avans Talep Formu - Specific Fields */}
            {selectedForm?.id === 'avans' && (
              <>
                {/* Validation Error Alert */}
                {employeeInfo && !employeeInfo.can_request_advance && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-red-800">
                      {employeeInfo.advance_validation_error}
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Auto-fill info banner */}
                <div className="p-4 bg-[hsl(var(--brand-50))] border border-[hsl(var(--brand-200))] rounded-md">
                  <p className="text-sm text-[hsl(var(--brand-800))]">
                    <strong>Not:</strong> Talebiniz önce şube yöneticinize, ardından İK'ya iletilecektir.
                  </p>
                </div>
                
                {/* Auto-filled fields - Read Only */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <div>
                      <Label className="text-xs text-[hsl(var(--muted-foreground))]">Talep Eden</Label>
                      <p className="font-medium text-[hsl(var(--foreground))]" data-testid="auto-requester-name">
                        {formData.talepEdenAdi || 'Yükleniyor...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <div>
                      <Label className="text-xs text-[hsl(var(--muted-foreground))]">Talep Tarihi</Label>
                      <p className="font-medium text-[hsl(var(--foreground))]" data-testid="auto-request-date">
                        {formData.talepTarihi || 'Yükleniyor...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-green-600" />
                    <div>
                      <Label className="text-xs text-green-600 font-medium">Ödeme Tarihi (Çarşamba)</Label>
                      <p className="font-medium text-green-700" data-testid="auto-payment-date">
                        {formData.odemeTarihi || 'Hesaplanıyor...'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Max advance info */}
                {employeeInfo?.max_advance_amount && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-1">
                    <p className="text-sm text-amber-800">
                      <strong>Bilgi:</strong> Maaşınızın %{Math.round(employeeInfo.max_avans_yuzdesi || 0)}'u olan maksimum <strong>{employeeInfo.max_advance_amount.toLocaleString('tr-TR')} TL</strong> avans talebinde bulunabilirsiniz.
                    </p>
                    {employeeInfo.yillik_avans_adedi > 0 && (
                      <p className="text-sm text-amber-800">
                        <strong>Yıllık Hak:</strong> Bu yıl <strong>{Math.max(0, employeeInfo.yillik_avans_adedi - (employeeInfo.yearly_advance_count || 0))}</strong> avans hakkınız kaldı. 
                        <span className="text-amber-600 text-xs ml-1">(Sadece onaylanan talepler haktan düşer)</span>
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="avansMiktari">Avans Miktarı (TL) *</Label>
                  <Input
                    id="avansMiktari"
                    type="number"
                    min="1"
                    max={employeeInfo?.max_advance_amount || undefined}
                    placeholder={`Örn: ${employeeInfo?.max_advance_amount ? Math.floor(employeeInfo.max_advance_amount / 2).toLocaleString('tr-TR') : '5000'}`}
                    value={formData.avansMiktari || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, avansMiktari: value });
                      // Show immediate warning if exceeds limit
                      if (employeeInfo?.max_advance_amount && parseInt(value) > employeeInfo.max_advance_amount) {
                        // Warning will be shown below via the condition
                      }
                    }}
                    data-testid="form-field-avans-miktari"
                    className={`border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))] ${
                      employeeInfo?.max_advance_amount && formData.avansMiktari && parseInt(formData.avansMiktari) > employeeInfo.max_advance_amount 
                        ? 'border-red-500 ring-red-500' 
                        : ''
                    }`}
                    disabled={employeeInfo && !employeeInfo.can_request_advance}
                  />
                  {employeeInfo?.max_advance_amount && formData.avansMiktari && parseInt(formData.avansMiktari) > employeeInfo.max_advance_amount && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 font-medium">
                        ⚠️ Maaşınızın %{Math.round(employeeInfo.max_avans_yuzdesi || 0)}'undan fazla avans talep edemezsiniz!
                      </p>
                      <p className="text-sm text-red-600 mt-1">
                        Maaşınız: <strong>{employeeInfo.current_salary?.toLocaleString('tr-TR')} TL</strong> | 
                        Maksimum Avans (%{Math.round(employeeInfo.max_avans_yuzdesi || 0)}): <strong>{employeeInfo.max_advance_amount.toLocaleString('tr-TR')} TL</strong>
                      </p>
                      <p className="text-sm text-red-600 mt-1">
                        Talep ettiğiniz miktar: <strong>{parseInt(formData.avansMiktari).toLocaleString('tr-TR')} TL</strong> 
                        ({Math.round((parseInt(formData.avansMiktari) / employeeInfo.current_salary) * 100)}% - Limit aşımı!)
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="taksitSayisi">Taksit Sayısı *{employeeInfo?.max_taksit_sayisi ? ` (Max ${employeeInfo.max_taksit_sayisi})` : ''}</Label>
                  <Select
                    value={formData.taksitSayisi || ''}
                    onValueChange={(value) => setFormData({ ...formData, taksitSayisi: value })}
                    disabled={employeeInfo && !employeeInfo.can_request_advance}
                  >
                    <SelectTrigger 
                      id="taksitSayisi"
                      data-testid="form-field-taksit-sayisi"
                      className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                    >
                      <SelectValue placeholder="Taksit sayısını seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: employeeInfo?.max_taksit_sayisi > 0 ? employeeInfo.max_taksit_sayisi : 3 }, (_, i) => i + 1).map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n === 1 ? '1 Taksit (Tek Seferde)' : `${n} Taksit`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="avansSebebi">Avans Sebebi *</Label>
                  <Textarea
                    id="avansSebebi"
                    placeholder="Avans talep etme nedeninizi detaylı olarak açıklayın..."
                    value={formData.avansSebebi || ''}
                    onChange={(e) => setFormData({ ...formData, avansSebebi: e.target.value })}
                    rows={4}
                    data-testid="form-field-avans-sebebi"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                    disabled={employeeInfo && !employeeInfo.can_request_advance}
                  />
                </div>

                {/* Islak İmzalı Belge Yükleme - ZORUNLU */}
                <div className="space-y-2">
                  <Label htmlFor="islakImzaBelgesi" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Islak İmzalı Formu Yükleyin <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="islakImzaBelgesi"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormData((prev) => ({
                            ...prev,
                            islakImzaBelgesi: { name: file.name, data: reader.result, type: file.type }
                          }));
                        };
                        reader.readAsDataURL(file);
                      } else {
                        setFormData((prev) => ({ ...prev, islakImzaBelgesi: null }));
                      }
                    }}
                    data-testid="form-field-islak-imza"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[hsl(var(--brand-50))] file:text-[hsl(var(--brand-700))] hover:file:bg-[hsl(var(--brand-100))]"
                    disabled={employeeInfo && !employeeInfo.can_request_advance}
                  />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    PDF, JPG veya PNG formatında yükleyebilirsiniz.
                  </p>
                </div>

                {/* Uyarı Notu */}
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Önemli Not:
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    Talep onaylandıktan sonra ıslak imzalı formu yöneticinize teslim ediniz. 
                    <strong className="text-orange-900"> Islak imza teslim edilmeyen başvurular iptal edilir.</strong>
                  </p>
                </div>
              </>
            )}

            {/* Generic form fields for other forms (Overtime, Leave) */}
            {selectedForm?.id === 'overtime' && (
              <>
                {/* Auto-filled info banner */}
                <div className="p-4 bg-[hsl(var(--brand-50))] border border-[hsl(var(--brand-200))] rounded-md">
                  <p className="text-sm text-[hsl(var(--brand-800))]">
                    <strong>Not:</strong> Fazla mesai talebiniz önce şube yöneticinize, ardından İK'ya iletilecektir.
                  </p>
                </div>
                
                {/* Auto-filled fields - Read Only */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <div>
                      <Label className="text-xs text-[hsl(var(--muted-foreground))]">Mesai Yapan</Label>
                      <p className="font-medium text-[hsl(var(--foreground))]" data-testid="auto-overtime-name">
                        {formData.mesaiYapanAdi || 'Yükleniyor...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <div>
                      <Label className="text-xs text-[hsl(var(--muted-foreground))]">Form Tarihi</Label>
                      <p className="font-medium text-[hsl(var(--foreground))]" data-testid="auto-overtime-form-date">
                        {formData.formOlusturmaTarihi || 'Yükleniyor...'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="mesaiTarihi">Mesai Yapılan Tarih *</Label>
                  <Input
                    id="mesaiTarihi"
                    type="date"
                    value={formData.mesaiTarihi || ''}
                    onChange={(e) => setFormData({ ...formData, mesaiTarihi: e.target.value })}
                    data-testid="form-field-mesai-tarihi"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                  />
                </div>

                <div>
                  <Label htmlFor="mesaiSaati">Mesai Saati (Saat) *</Label>
                  <Input
                    id="mesaiSaati"
                    type="number"
                    min="1"
                    max="24"
                    placeholder="Örn: 3"
                    value={formData.mesaiSaati || ''}
                    onChange={(e) => setFormData({ ...formData, mesaiSaati: e.target.value })}
                    data-testid="form-field-mesai-saati"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                  />
                </div>

                <div>
                  <Label>Mesai Günü *</Label>
                  <div className="flex gap-3 mt-2">
                    <Button
                      type="button"
                      variant={formData.mesaiGunu === 'is_gunu' ? 'default' : 'outline'}
                      className={`flex-1 ${formData.mesaiGunu === 'is_gunu' ? 'bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white' : ''}`}
                      onClick={() => setFormData({ ...formData, mesaiGunu: 'is_gunu' })}
                      data-testid="form-field-mesai-is-gunu"
                    >
                      <Briefcase className="h-4 w-4 mr-2" />
                      İş Günü
                    </Button>
                    <Button
                      type="button"
                      variant={formData.mesaiGunu === 'tatil' ? 'default' : 'outline'}
                      className={`flex-1 ${formData.mesaiGunu === 'tatil' ? 'bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white' : ''}`}
                      onClick={() => setFormData({ ...formData, mesaiGunu: 'tatil' })}
                      data-testid="form-field-mesai-tatil"
                    >
                      <PartyPopper className="h-4 w-4 mr-2" />
                      Tatil
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="mesaiNedeni">Mesai Nedeni *</Label>
                  <Textarea
                    id="mesaiNedeni"
                    placeholder="Fazla mesai yapma nedeninizi detaylı olarak açıklayın..."
                    value={formData.mesaiNedeni || ''}
                    onChange={(e) => setFormData({ ...formData, mesaiNedeni: e.target.value })}
                    rows={4}
                    data-testid="form-field-mesai-nedeni"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                  />
                </div>

                {/* Form Yükle Alanı */}
                <div className="space-y-2">
                  <Label htmlFor="mesaiFormDosya">Form Yükle *</Label>
                  <Input
                    id="mesaiFormDosya"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormData({ 
                            ...formData, 
                            mesaiFormDosya: reader.result,
                            mesaiFormDosyaAdi: file.name 
                          });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    data-testid="form-field-mesai-dosya"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                  />
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Yöneticinizin ve sizin ıslak imzalı formunuzu iletmeniz gerekmektedir.
                  </p>
                  {formData.mesaiFormDosyaAdi && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      Yüklenen: {formData.mesaiFormDosyaAdi}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Leave form */}
            {selectedForm?.id === 'leave' && (
              <>
                {/* Auto-filled info banner */}
                <div className="p-4 bg-[hsl(var(--brand-50))] border border-[hsl(var(--brand-200))] rounded-md">
                  <p className="text-sm text-[hsl(var(--brand-800))]">
                    <strong>Not:</strong> İzin talebiniz önce şube yöneticinize, ardından İK'ya iletilecektir.
                  </p>
                </div>
                
                {/* Auto-filled fields - Read Only */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <div>
                      <Label className="text-xs text-[hsl(var(--muted-foreground))]">Talep Eden</Label>
                      <p className="font-medium text-[hsl(var(--foreground))]" data-testid="auto-leave-name">
                        {formData.talepEdenAdi || 'Yükleniyor...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                    <div>
                      <Label className="text-xs text-[hsl(var(--muted-foreground))]">Talep Tarihi</Label>
                      <p className="font-medium text-[hsl(var(--foreground))]" data-testid="auto-leave-date">
                        {formData.talepTarihi || 'Yükleniyor...'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* İzin Türü */}
                <div>
                  <Label>İzin Türü *</Label>
                  <div className="flex gap-3 mt-2">
                    <Button
                      type="button"
                      variant={formData.izinTuru === 'yillik' ? 'default' : 'outline'}
                      className={`flex-1 ${formData.izinTuru === 'yillik' ? 'bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white' : ''}`}
                      onClick={() => setFormData({ ...formData, izinTuru: 'yillik' })}
                      data-testid="form-field-izin-yillik"
                    >
                      <Palmtree className="h-4 w-4 mr-2" />
                      Yıllık İzin
                    </Button>
                    <Button
                      type="button"
                      variant={formData.izinTuru === 'mazeret' ? 'default' : 'outline'}
                      className={`flex-1 ${formData.izinTuru === 'mazeret' ? 'bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white' : ''}`}
                      onClick={() => setFormData({ ...formData, izinTuru: 'mazeret' })}
                      data-testid="form-field-izin-mazeret"
                    >
                      <FileQuestion className="h-4 w-4 mr-2" />
                      Ücretli İzin
                    </Button>
                    <Button
                      type="button"
                      variant={formData.izinTuru === 'ucretsiz' ? 'default' : 'outline'}
                      className={`flex-1 ${formData.izinTuru === 'ucretsiz' ? 'bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white' : ''}`}
                      onClick={() => setFormData({ ...formData, izinTuru: 'ucretsiz' })}
                      data-testid="form-field-izin-ucretsiz"
                    >
                      <BanknoteIcon className="h-4 w-4 mr-2" />
                      Ücretsiz İzin
                    </Button>
                  </div>
                </div>

                {/* İzin Tarihleri */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="izinBaslangic">İzin Başlangıç Tarihi *</Label>
                    <Input
                      id="izinBaslangic"
                      type="date"
                      value={formData.izinBaslangic || ''}
                      onChange={(e) => setFormData({ ...formData, izinBaslangic: e.target.value })}
                      data-testid="form-field-izin-baslangic"
                      className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="izinBitis">İzin Bitiş Tarihi *</Label>
                    <Input
                      id="izinBitis"
                      type="date"
                      value={formData.izinBitis || ''}
                      onChange={(e) => setFormData({ ...formData, izinBitis: e.target.value })}
                      min={formData.izinBaslangic || undefined}
                      data-testid="form-field-izin-bitis"
                      className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                    />
                  </div>
                </div>

                {/* İzin Gün Hesabı (kurallara göre) + Kalan Bakiye + Aşım */}
                {formData.izinTuru === 'yillik' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md space-y-3" data-testid="leave-calc-box">
                    {/* Kalan yıllık izin */}
                    {leaveBalance && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-blue-900 font-medium">Kalan Yıllık İzin Hakkınız ({leaveBalance.year})</span>
                        <span className="text-lg font-bold text-blue-700" data-testid="leave-remaining">
                          {leaveBalance.remaining_annual_leave} gün
                        </span>
                      </div>
                    )}
                    {calcLoading && <p className="text-xs text-blue-700">Hesaplanıyor...</p>}
                    {/* Hesap tablosu */}
                    {leaveCalc && (
                      <div className="border-t border-blue-200 pt-3 space-y-1.5" data-testid="leave-calc-table">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Seçilen takvim günü</span>
                          <span className="font-medium">{leaveCalc.total_calendar_days} gün</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Sayılan izin günü
                            <span className="text-xs text-gray-400 ml-1">
                              ({leaveCalc.rules.cumartesi_dahil ? 'Cmt dahil' : 'Cmt hariç'}, Pazar hariç, {leaveCalc.rules.tatil_dahil ? 'tatil dahil' : 'tatil hariç'})
                            </span>
                          </span>
                          <span className="font-semibold text-blue-800" data-testid="leave-counted-days">{leaveCalc.counted_days} gün</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Yıllık izinden düşülecek</span>
                          <span className="font-medium text-emerald-700">{leaveCalc.annual_portion} gün</span>
                        </div>
                        {leaveCalc.exceeds && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Yıllık izni aşan</span>
                            <span className="font-bold text-red-600" data-testid="leave-excess-days">{leaveCalc.excess_days} gün</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Aşım uyarısı + ücretli/ücretsiz/avans seçimi */}
                    {leaveCalc && leaveCalc.exceeds && (
                      <div className="border-t border-red-200 pt-3 space-y-2" data-testid="leave-excess-box">
                        <p className="text-sm text-red-700 font-medium">
                          ⚠️ Talebiniz kalan yıllık izninizi {leaveCalc.excess_days} gün aşıyor.
                          Aşan günleri nasıl kullanmak istersiniz?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {leaveCalc.rules?.avans_izin && (
                            <Button
                              type="button"
                              size="sm"
                              variant={formData.asanTuru === 'avans' ? 'default' : 'outline'}
                              className={`flex-1 min-w-[140px] ${formData.asanTuru === 'avans' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                              onClick={() => setFormData({ ...formData, asanTuru: 'avans' })}
                              data-testid="leave-excess-avans"
                            >
                              Avans Yıllık İzin ({leaveCalc.excess_days} gün)
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant={formData.asanTuru === 'ucretli' ? 'default' : 'outline'}
                            className={`flex-1 min-w-[140px] ${formData.asanTuru === 'ucretli' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
                            onClick={() => setFormData({ ...formData, asanTuru: 'ucretli' })}
                            data-testid="leave-excess-ucretli"
                          >
                            Ücretli İzin ({leaveCalc.excess_days} gün)
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={formData.asanTuru === 'ucretsiz' ? 'default' : 'outline'}
                            className={`flex-1 min-w-[140px] ${formData.asanTuru === 'ucretsiz' ? 'bg-gray-700 hover:bg-gray-800 text-white' : ''}`}
                            onClick={() => setFormData({ ...formData, asanTuru: 'ucretsiz' })}
                            data-testid="leave-excess-ucretsiz"
                          >
                            Ücretsiz İzin ({leaveCalc.excess_days} gün)
                          </Button>
                        </div>
                        {formData.asanTuru === 'avans' && (
                          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2" data-testid="leave-avans-note">
                            ℹ️ {leaveCalc.excess_days} gün <strong>avans yıllık izin</strong> olarak kullanılacak ve ileride hak edeceğiniz yıllık izinden düşülecektir.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* Diğer izin türleri için basit toplam */}
                {formData.izinTuru !== 'yillik' && formData.izinBaslangic && formData.izinBitis && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>Toplam İzin Süresi:</strong> {
                        Math.ceil((new Date(formData.izinBitis) - new Date(formData.izinBaslangic)) / (1000 * 60 * 60 * 24)) + 1
                      } gün
                    </p>
                  </div>
                )}

                {/* İzin Nedeni */}
                <div>
                  <Label htmlFor="izinNedeni">İzin Nedeni</Label>
                  <Textarea
                    id="izinNedeni"
                    placeholder="İzin talebinizin nedenini açıklayın..."
                    value={formData.izinNedeni || ''}
                    onChange={(e) => setFormData({ ...formData, izinNedeni: e.target.value })}
                    rows={3}
                    data-testid="form-field-izin-nedeni"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                  />
                </div>

                {/* Belge Yükleme - Çoklu - ZORUNLU */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Islak İmzalı Form <span className="text-red-500">*</span> (Birden fazla yüklenebilir)
                  </Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const readFiles = [];
                      let processed = 0;
                      
                      files.forEach((file) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          readFiles.push({
                            name: file.name,
                            data: reader.result,
                            type: file.type
                          });
                          processed++;
                          if (processed === files.length) {
                            setFormData({ 
                              ...formData, 
                              izinBelgeleri: [...(formData.izinBelgeleri || []), ...readFiles]
                            });
                          }
                        };
                        reader.readAsDataURL(file);
                      });
                    }}
                    data-testid="form-field-izin-belgeler"
                    className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[hsl(var(--brand-50))] file:text-[hsl(var(--brand-700))] hover:file:bg-[hsl(var(--brand-100))]"
                  />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Sağlık raporu, evlilik cüzdanı, vb. destekleyici belgelerinizi yükleyebilirsiniz.
                  </p>
                  
                  {/* Yüklenen Belgeler Listesi */}
                  {formData.izinBelgeleri && formData.izinBelgeleri.length > 0 && (
                    <div className="space-y-2 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm font-medium text-green-800">Yüklenen Belgeler:</p>
                      {formData.izinBelgeleri.map((belge, index) => (
                        <div key={index} className="flex items-center justify-between text-sm text-green-700">
                          <span className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            {belge.name}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = formData.izinBelgeleri.filter((_, i) => i !== index);
                              setFormData({ ...formData, izinBelgeleri: updated });
                            }}
                            className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFillFormModal(false)} data-testid="cancel-fill-form">
              İptal
            </Button>
            <Button 
              onClick={handleSubmitRequest}
              disabled={
                loading ||
                (selectedForm?.id === 'avans' && (
                  (employeeInfo && !employeeInfo.can_request_advance) || !formData.islakImzaBelgesi
                )) ||
                (selectedForm?.id === 'leave' && (!formData.izinBelgeleri || formData.izinBelgeleri.length === 0)) ||
                (selectedForm?.id === 'overtime' && (
                  !formData.mesaiTarihi ||
                  !formData.mesaiSaati || Number(formData.mesaiSaati) <= 0 ||
                  !formData.mesaiGunu ||
                  !formData.mesaiNedeni || formData.mesaiNedeni.trim() === '' ||
                  !formData.mesaiFormDosya
                ))
              }
              data-testid="submit-request-btn"
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white disabled:opacity-50"
            >
              {loading ? 'Gönderiliyor...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Talep Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Detail Modal */}
      <Dialog open={showRequestDetailModal} onOpenChange={setShowRequestDetailModal}>
        <DialogContent className="max-w-2xl" data-testid="request-detail-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Talep Detayı</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4" style={{ fontFamily: 'Karla, sans-serif' }}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[hsl(var(--muted-foreground))]">Form</Label>
                  <p className="font-medium text-[hsl(var(--foreground))]">{selectedRequest.form_template_name}</p>
                </div>
                <div>
                  <Label className="text-[hsl(var(--muted-foreground))]">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
                <div>
                  <Label className="text-[hsl(var(--muted-foreground))]">Gönderilme Tarihi</Label>
                  <p className="font-medium text-[hsl(var(--foreground))]">{formatDate(selectedRequest.created_at)}</p>
                </div>
              </div>

              {selectedRequest.notes && (
                <div>
                  <Label className="text-[hsl(var(--muted-foreground))]">Notlarınız</Label>
                  <p className="text-sm p-3 bg-[hsl(var(--muted))] rounded-md mt-1">{selectedRequest.notes}</p>
                </div>
              )}

              {selectedRequest.form_data && (
                <div>
                  <Label className="text-[hsl(var(--muted-foreground))]">Gönderdiğiniz Bilgiler</Label>
                  <div className="p-4 bg-[hsl(var(--muted))] rounded-md mt-1 space-y-2">
                    {(() => {
                      // Flatten nested form_data if exists
                      const formData = selectedRequest.form_data.form_data || selectedRequest.form_data;
                      const fieldLabels = {
                        'avansMiktari': 'Avans Miktarı',
                        'taksitSayisi': 'Taksit Sayısı',
                        'avansSebebi': 'Avans Sebebi',
                        'talepEdenAdi': 'Talep Eden',
                        'talepTarihi': 'Talep Tarihi',
                        'amount': 'Tutar',
                        'reason': 'Neden',
                        'izinTuru': 'İzin Türü',
                        'izinBaslangic': 'Başlangıç Tarihi',
                        'izinBitis': 'Bitiş Tarihi',
                        'izinNedeni': 'İzin Nedeni',
                        'mesaiTarihi': 'Mesai Tarihi',
                        'mesaiSaati': 'Mesai Saati',
                        'mesaiGunu': 'Mesai Günü',
                        'mesaiNedeni': 'Mesai Nedeni'
                      };
                      const valueLabels = {
                        'yillik': 'Yıllık İzin',
                        'mazeret': 'Ücretli İzin',
                        'ucretsiz': 'Ücretsiz İzin',
                        'is_gunu': 'Hafta İçi',
                        'tatil': 'Tatil Günü'
                      };
                      return Object.entries(formData).map(([key, value]) => {
                        // Skip nested objects, arrays and empty values
                        if (typeof value === 'object' || value === null || value === undefined || key === 'izinBelgeleri' || key === 'mesaiFormDosya') return null;
                        const displayLabel = fieldLabels[key] || key;
                        const displayValue = valueLabels[value] || 
                                            (key === 'avansMiktari' ? `${value} TL` : 
                                            key === 'taksitSayisi' ? `${value} Taksit` : 
                                            key === 'mesaiSaati' ? `${value} saat` : String(value));
                        return (
                          <div key={key} className="flex justify-between text-sm">
                            <span className="font-medium">{displayLabel}:</span>
                            <span className="text-right max-w-[60%]">{displayValue}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* Yüklenen Belgeler */}
              {selectedRequest.form_data && (() => {
                const formData = selectedRequest.form_data.form_data || selectedRequest.form_data;
                const belgeler = formData.izinBelgeleri || [];
                const mesaiDosya = formData.mesaiFormDosya;
                const islakImza = formData.islakImzaBelgesi;

                if (belgeler.length === 0 && !mesaiDosya && !(islakImza && islakImza.data)) return null;

                return (
                  <div>
                    <Label className="text-[hsl(var(--muted-foreground))]">Yüklenen Belgeler</Label>
                    <div className="p-4 bg-gray-50 rounded-md mt-1 space-y-2 border">
                      {islakImza && islakImza.data && (
                        <div className="flex items-center justify-between bg-white p-2 rounded border" data-testid="view-islak-imza-belge">
                          <span className="text-sm flex items-center gap-2">
                            <FileText className="h-4 w-4 text-emerald-600" />
                            {islakImza.name || 'Islak İmzalı Belge'}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(islakImza.data, '_blank')}
                              title="Görüntüle"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = islakImza.data;
                                link.download = islakImza.name || 'islak_imza';
                                link.click();
                              }}
                              title="İndir"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                      {belgeler.map((belge, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                          <span className="text-sm flex items-center gap-2">
                            <FileText className="h-4 w-4 text-cyan-600" />
                            {belge.name}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(belge.data, '_blank')}
                              title="Görüntüle"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = belge.data;
                                link.download = belge.name;
                                link.click();
                              }}
                              title="İndir"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {mesaiDosya && (
                        <div className="flex items-center justify-between bg-white p-2 rounded border">
                          <span className="text-sm flex items-center gap-2">
                            <FileText className="h-4 w-4 text-amber-600" />
                            Mesai Form Dosyası
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const url = mesaiDosya.startsWith('data:') ? mesaiDosya : `data:application/octet-stream;base64,${mesaiDosya}`;
                                window.open(url, '_blank');
                              }}
                              title="Görüntüle"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const url = mesaiDosya.startsWith('data:') ? mesaiDosya : `data:application/octet-stream;base64,${mesaiDosya}`;
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = 'mesai_form.pdf';
                                link.click();
                              }}
                              title="İndir"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {(selectedRequest.status_history || selectedRequest.history) && (selectedRequest.status_history || selectedRequest.history).length > 0 && (
                <div>
                  <Label className="text-[hsl(var(--muted-foreground))]">İşlem Geçmişi</Label>
                  <div className="space-y-2 mt-2">
                    {(selectedRequest.status_history || selectedRequest.history).map((entry, idx) => {
                      const statusLabels = {
                        'pending_branch': 'Şube Onayı Bekliyor',
                        'pending_hr': 'İK Onayı Bekliyor',
                        'pending_management': 'Yönetim Onayı Bekliyor',
                        'approved': 'Onaylandı',
                        'rejected_by_branch': 'Şube Reddetti',
                        'rejected_by_hr': 'İK Reddetti',
                        'rejected_by_management': 'Yönetim Reddetti'
                      };
                      const isApproved = entry.status === 'approved' || entry.status?.includes('pending') || entry.action === 'approved';
                      const isRejected = entry.status?.includes('rejected') || entry.action === 'rejected';
                      const statusLabel = statusLabels[entry.status] || entry.status || entry.action;
                      const byName = entry.by_name || entry.action_by_name || 'Sistem';
                      const timestamp = entry.timestamp || entry.action_at;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-md text-sm ${
                            isRejected 
                              ? 'bg-red-50 border border-red-200' 
                              : entry.status === 'approved'
                                ? 'bg-green-50 border border-green-200'
                                : 'bg-blue-50 border border-blue-200'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-[hsl(var(--foreground))]">{byName}</span>
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">{formatDate(timestamp)}</span>
                          </div>
                          <p className={isRejected ? 'text-red-700 mt-1' : entry.status === 'approved' ? 'text-green-700 mt-1' : 'text-blue-700 mt-1'}>
                            <strong>{statusLabel}</strong>
                            {entry.note && `: ${entry.note}`}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDetailModal(false)} data-testid="close-detail-modal">
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Response Modal */}
      <Dialog open={showDocResponseModal} onOpenChange={setShowDocResponseModal}>
        <DialogContent className="max-w-2xl" data-testid="doc-response-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Belge Gönder - {selectedDocRequest?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDocRequest && (
            <div className="space-y-4" style={{ fontFamily: 'Karla, sans-serif' }}>
              {/* Request Info */}
              <div className="p-4 bg-[hsl(var(--muted))] rounded-lg">
                <h4 className="font-semibold mb-2 text-[hsl(var(--foreground))]">Talep Detayı</h4>
                {selectedDocRequest.description && (
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">
                    {selectedDocRequest.description}
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm text-[hsl(var(--muted-foreground))]">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {selectedDocRequest.created_by_name || 'İK'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date(selectedDocRequest.created_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                
                {/* Attached documents from HR */}
                {selectedDocRequest.documents && selectedDocRequest.documents.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
                    <p className="text-sm font-medium mb-2">İK'dan Gelen Belgeler:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedDocRequest.documents.map((doc, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = doc.data;
                            link.download = doc.name;
                            link.click();
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          {doc.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Belge Yükle *
                </Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  multiple
                  onChange={handleResponseFileChange}
                  data-testid="response-file-input"
                  className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[hsl(var(--brand-50))] file:text-[hsl(var(--brand-700))] hover:file:bg-[hsl(var(--brand-100))]"
                />
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  PDF, JPG, PNG, DOC formatlarında birden fazla dosya yükleyebilirsiniz.
                </p>
              </div>

              {/* Selected Files List */}
              {responseFiles.length > 0 && (
                <div className="space-y-2 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm font-medium text-green-800">Yüklenecek Belgeler:</p>
                  {responseFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between text-sm text-green-700 bg-white p-2 rounded border border-green-200">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        {file.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveResponseFile(index)}
                        className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-file-${index}`}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="responseMessage">Mesaj (Opsiyonel)</Label>
                <Textarea
                  id="responseMessage"
                  placeholder="İK'ya iletmek istediğiniz bir not varsa yazabilirsiniz..."
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  rows={3}
                  data-testid="response-message"
                  className="border-[hsl(var(--input))] focus-visible:ring-[hsl(var(--ring))]"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDocResponseModal(false);
                setSelectedDocRequest(null);
                setResponseFiles([]);
                setResponseMessage('');
              }}
              data-testid="cancel-doc-response"
            >
              İptal
            </Button>
            <Button
              onClick={handleSubmitDocResponse}
              disabled={responseLoading || responseFiles.length === 0}
              data-testid="submit-doc-response"
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))] text-white disabled:opacity-50"
            >
              {responseLoading ? 'Gönderiliyor...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Belgeleri Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRRequests;